import * as mongoose from 'mongoose';
import { modelOptions, prop, Severity } from '@typegoose/typegoose';

enum RegistrationStatus {
  NA = 0,
  LICENSED = 2,
  EXPIRED = 3,
}

enum LicenseType {
  LICENSED = 0,
  TRIAL = 1,
  EXPIRED = 2,
}

enum ENDPOINT_PATCH_STATUS {
  UNKNOWN = 0,
  UP_TO_DATE = 1,
  MISSING_SECURITY = 2,
  MISSING_NON_SECURITY = 3,
}

enum EndpointEnginesScanModeEnum {
  NO_AV = 0,
  REMOTE_ONLY = 1,
  REMOTE_WITH_FALLBACK_ON_LIGHT = 2,
  REMOTE_WITH_FALLBACK_ON_FULL = 3,
  LIGHT_ONLY = 4,
  FULL_ONLY = 5,
}

class EndpointEnginesScanMode {
  @prop({ required: true, enum: EndpointEnginesScanModeEnum })
  current: EndpointEnginesScanModeEnum;

  @prop({ required: true, enum: EndpointEnginesScanModeEnum })
  requested: EndpointEnginesScanModeEnum;
}

@modelOptions({
  // we're not interested in the types of heuristics and sandboxing - only that they exist
  options: { allowMixed: Severity.ALLOW },
})
class Feature {
  @prop({ required: true })
  id: string;

  @prop({ required: true })
  enabled: boolean;

  @prop({ required: false, type: mongoose.Schema.Types.Mixed })
  heuristics?: object;

  @prop({ required: false, type: mongoose.Schema.Types.Mixed })
  sandboxing?: object;

  @prop({ required: false })
  registrationStatus?: string;

  @prop({ required: false })
  isFunctioning?: boolean;

  @prop({ required: false, type: EndpointEnginesScanMode })
  scanMode: EndpointEnginesScanMode;
}

class SvaUsageInfo {
  @prop({ required: true })
  svaLoad: string;
}

class ProductInfoAlert {
  @prop({ required: true })
  name: string;

  @prop({ required: true })
  severity: string;
}

class ProductInfo {
  @prop({ required: false })
  svaUsage?: SvaUsageInfo;

  @prop({ required: true })
  lastSignatureUpdate: Date;

  @prop({ required: true, type: Feature })
  features: Array<Feature>;

  @prop({ required: false, type: ProductInfoAlert })
  alerts?: Array<ProductInfoAlert>;

  @prop({ required: false, enum: LicenseType })
  registerStatus: LicenseType;

  @prop({ required: true })
  automaticUpdateForProduct: boolean;

  @prop({ required: true })
  automaticUpdateForSignatures: boolean;

  @prop({ required: true })
  productUpdateStatus: number;

  @prop({ required: true })
  lastEndPointUpdate: Date;
}

class PatchManagementInfoModel {
  @prop({ required: false })
  lastAssessmentDate?: Date;

  @prop({ required: false, enum: ENDPOINT_PATCH_STATUS, type: mongoose.Schema.Types.Number })
  patchStatus?: ENDPOINT_PATCH_STATUS;
}

class Specifics {
  @prop({ required: true, type: ProductInfo })
  productInfo: ProductInfo;

  @prop({ required: false })
  maCertFingerprint?: string;

  @prop({ required: false })
  maCertStatus?: number;

  @prop({ required: false })
  maUsesFallback?: boolean;

  @prop({ required: false })
  hasRemoteAccess?: boolean;

  @prop({ required: false })
  isContainerHost?: boolean;

  @prop({ required: false })
  pendingRestart?: boolean;

  @prop({ required: false })
  pendingRestartPatchManagement?: boolean;

  @prop({ required: false })
  patchManagementInfo?: PatchManagementInfoModel;

  @prop({ required: false })
  isPowerUser?: boolean;

  @prop({ required: false })
  hasCloudConnectivity?: boolean;
}

class AssignedPolicy {
  @prop({ required: true, type: mongoose.Types.ObjectId })
  policyId: mongoose.Types.ObjectId;

  @prop({ required: true })
  policyName: string;
}

class ClientPolicy {
  @prop({ required: true, type: mongoose.Types.ObjectId })
  policyId: mongoose.Types.ObjectId;

  @prop({ required: true })
  policyName: string;
}

class EndpointMalwareSpecifics {
  @prop({ required: true })
  malwarePresent: boolean;
}

class EndpointFeatureDetails {
  @prop({ required: true })
  installed: boolean;

  @prop({ required: true })
  enabled: boolean;

  @prop({ required: true, enum: RegistrationStatus })
  registrationStatus: RegistrationStatus;
}

class EndpointFeaturesSpecifics {
  @prop({ required: false, type: EndpointFeatureDetails })
  atc?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  antimalware?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  appControl?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  contentControl?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  deviceControl?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  encryption?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  edr?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  exchange?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  exchangeTasks?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  exploitMitigation?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  firewall?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  hyperDetect?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  integrityMonitor?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  patchManagement?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  patchManagementServer?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  powerUser?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  relay?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  remoteEnginesScanning?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  sandbox?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  networkMonitor?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  riskManagement?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  containerProtection?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  remoteAccess?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  liveSearch?: EndpointFeatureDetails;

  @prop({ required: false, type: EndpointFeatureDetails })
  nsvaSensor?: EndpointFeatureDetails;
}

class EndpointIsolationSpecifics {
  @prop({ required: true })
  status: boolean;
}

class EndpointLicenseInfo {
  @prop({ required: true, enum: LicenseType })
  type: LicenseType;
}

class EndpointLicenseSpecifics {
  @prop({ required: false, type: EndpointLicenseInfo })
  licenseInfo?: EndpointLicenseInfo;
}

@modelOptions({
  schemaOptions: { collection: 'endpoints' },
  options: { allowMixed: Severity.ALLOW, customName: 'endpoints' },
})
export class EndpointsModel {
  @prop({ required: true })
  _id: mongoose.Types.ObjectId;

  @prop({ required: true })
  name: string;

  @prop({ required: true })
  appId: number;

  @prop({ required: true })
  operatingSystem: number;

  @prop({ required: true, type: mongoose.Types.ObjectId })
  companyId: mongoose.Types.ObjectId;

  @prop({ required: true, type: Specifics })
  specifics: Specifics;

  @prop({ required: false, type: AssignedPolicy })
  assignedPolicy: AssignedPolicy;

  @prop({ required: false, type: ClientPolicy })
  clientPolicy: ClientPolicy;

  @prop({ required: false })
  isOnline: boolean;

  @prop({ required: false })
  lastSync?: Date;

  @prop({ required: false })
  deleted?: Date | null;

  @prop({ required: true })
  hardwareId: string;

  @prop({ required: false })
  lastRecordUpdate?: string;

  @prop({ required: false, type: EndpointMalwareSpecifics })
  malware?: EndpointMalwareSpecifics;

  @prop({ required: true, type: EndpointFeaturesSpecifics })
  features: EndpointFeaturesSpecifics;

  @prop({ required: false, type: EndpointIsolationSpecifics })
  isolation?: EndpointIsolationSpecifics;

  @prop({ required: false, type: EndpointLicenseSpecifics })
  license?: EndpointLicenseSpecifics;
}

export const MA_CERT_STATUS_AUTHORIZED = 5;
